/**
 * StrategicDecisionSheet - Post-completion verdict display for Strategic blueprint.
 *
 * Based on mockup_strategic.html. Shows structured strategic analysis results:
 * 1. Header bar (question + confidence pill + debate rounds + duration)
 * 2. Premise Challenge callout (amber border, hero differentiator)
 * 3. TL;DR callout (gold border, serif)
 * 4. Immediate Actions (3 cards from nextSteps)
 * 5. Structural Risk banner (from conflicts/tensions)
 * 6. Tabbed content: Summary | Ghost Deck | Debate | Workstreams
 *    - Summary: Confidence gauge, Headwinds/Tailwinds, Stress-tested assumptions
 *    - Ghost Deck: Slide sidebar + content viewer
 *    - Debate: Debater cards + round-by-round arguments
 *    - Workstreams: Workstream cards with progress + key findings
 */

import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Lightbulb,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  ShieldCheck,
  Layers,
  Target,
  DollarSign,
  RefreshCw,
  Shield,
} from 'lucide-react';
import type {
  StrategicIntensity,
  StrategicDeliverable,
  StressTestResult,
  StrategicWorkstream,
  StrategicConflict,
  DebateArgument,
  MediatorConsensus,
  GhostDeckSlide,
  CompetitorData,
} from '@/hooks/useStrategicStream';
import { extractStrategicTldr } from '@/utils/extractDecisionSheetData';

// ============================================================================
// TYPES
// ============================================================================

interface StrategicDecisionSheetProps {
  deliverable: StrategicDeliverable;
  stressTestResults: StressTestResult[];
  topic: string;
  intensity: StrategicIntensity;
  workstreams: StrategicWorkstream[];
  conflicts: StrategicConflict[];
  debateArguments: DebateArgument[];
  debateRounds: number;
  mediatorConsensus?: MediatorConsensus;
  ghostDeck: GhostDeckSlide[];
  competitorData: CompetitorData[];
  startedAt?: string;
  completedAt?: string;
  onNewResearch?: () => void;
  className?: string;
}

type TabId = 'summary' | 'ghost-deck' | 'debate' | 'workstreams';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function StrategicDecisionSheet({
  deliverable,
  stressTestResults,
  topic,
  intensity,
  workstreams,
  conflicts,
  debateArguments,
  debateRounds,
  mediatorConsensus,
  ghostDeck,
  competitorData: _competitorData,
  startedAt,
  completedAt,
  onNewResearch,
  className,
}: StrategicDecisionSheetProps) {
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const killSheet = deliverable.killSheet;

  // TL;DR
  const tldr = useMemo(() => extractStrategicTldr(deliverable), [deliverable]);

  // Confidence
  const confidenceLevel = killSheet?.confidenceLevel;
  const confidencePercent = useMemo(() => {
    if (!confidenceLevel) return undefined;
    const match = confidenceLevel.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : undefined;
  }, [confidenceLevel]);

  // Duration
  const durationText = useMemo(() => {
    if (startedAt && completedAt) {
      const seconds = Math.round((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000);
      if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
      return `${seconds}s`;
    }
    if (deliverable.totalLatencySeconds) {
      const s = Math.round(deliverable.totalLatencySeconds);
      if (s >= 60) return `${Math.floor(s / 60)}m ${s % 60}s`;
      return `${s}s`;
    }
    return undefined;
  }, [startedAt, completedAt, deliverable.totalLatencySeconds]);

  // Premise challenge from mediator tensions or conflicts
  const premiseChallenge = useMemo(() => {
    if (mediatorConsensus?.unresolvedTensions?.length) {
      return mediatorConsensus.unresolvedTensions[0];
    }
    const criticalConflict = conflicts.find(c => c.severity === 'critical');
    if (criticalConflict) return criticalConflict.description;
    return null;
  }, [mediatorConsensus, conflicts]);

  // Structural risk from conflicts
  const structuralRisk = useMemo(() => {
    const significant = conflicts.find(c => c.severity === 'significant' || c.severity === 'critical');
    return significant?.description || null;
  }, [conflicts]);

  // Next steps / immediate actions
  const nextSteps = killSheet?.nextSteps?.slice(0, 3) || [];

  // Action card icons
  const actionIcons = [
    <Target key="t" className="w-[18px] h-[18px] text-teal-500" />,
    <Lightbulb key="l" className="w-[18px] h-[18px] text-teal-500" />,
    <DollarSign key="d" className="w-[18px] h-[18px] text-teal-500" />,
  ];

  // Tab config
  const tabs: Array<{ id: TabId; label: string; show: boolean }> = [
    { id: 'summary', label: 'Summary', show: true },
    { id: 'ghost-deck', label: 'Ghost Deck', show: ghostDeck.length > 0 },
    { id: 'debate', label: 'Debate', show: debateArguments.length > 0 },
    { id: 'workstreams', label: 'Workstreams', show: workstreams.length > 0 },
  ];

  return (
    <div className={cn('w-full', className)}>
      {/* ── HEADER BAR ── */}
      <div className="pb-4 border-b border-border mb-6">
        <div className="flex items-start justify-between gap-5">
          <div className="text-base font-medium text-text-primary leading-relaxed flex-1">
            {topic}
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {confidencePercent !== undefined && (
              <span className="bg-verdict-primary/[0.18] border border-verdict-primary/35 text-verdict-primary text-[15px] font-bold px-3.5 py-1.5 rounded-full">
                {confidencePercent}%
              </span>
            )}
            {premiseChallenge && (
              <span className="inline-flex items-center gap-1.5 bg-verdict-primary/15 border border-verdict-primary/30 text-verdict-primary text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap">
                <Lightbulb className="w-3.5 h-3.5" />
                Premise Challenged
              </span>
            )}
          </div>
        </div>
        <div className="mt-2.5 text-xs text-text-muted">
          {workstreams.length} workstreams
          {debateRounds > 0 && <> &bull; {debateRounds} debate rounds</>}
          {durationText && <> &bull; {durationText}</>}
          {intensity && <> &bull; {intensity} mode</>}
        </div>
      </div>

      {/* ── PREMISE CHALLENGE (HERO) ── */}
      {premiseChallenge && (
        <div className="border-2 border-verdict-primary rounded-[14px] bg-verdict-primary/[0.04] p-7 mb-5 shadow-[0_0_30px_rgba(212,165,116,0.15),0_0_60px_rgba(212,165,116,0.05)] relative">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-6 h-6 text-verdict-primary" />
            <span className="text-[11px] uppercase text-verdict-primary tracking-[0.2em] font-semibold">
              Premise Challenge
            </span>
          </div>
          <div className="text-[21px] text-text-primary leading-relaxed">
            {premiseChallenge}
          </div>
        </div>
      )}

      {/* ── TL;DR CALLOUT ── */}
      <div className="border-l-4 border-verdict-primary bg-surface/70 backdrop-blur-xl rounded-r-xl px-6 py-5 mb-5">
        <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-text-muted mb-2.5">
          Strategic Recommendation
        </div>
        <div className="text-[19px] leading-relaxed text-text-primary">
          {tldr}
        </div>
        <div className="mt-3 text-xs text-text-muted">
          Stress-tested against {stressTestResults.length} challenge point{stressTestResults.length !== 1 ? 's' : ''}
          {confidencePercent !== undefined && <> &bull; {confidencePercent}% confidence</>}
        </div>
      </div>

      {/* ── IMMEDIATE ACTIONS ── */}
      {nextSteps.length > 0 && (
        <div className="mb-5">
          <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-text-secondary mb-3.5">
            Immediate Actions
          </div>
          <div className="flex gap-3">
            {nextSteps.map((step, i) => (
              <div key={i} className="flex-1 min-w-0 bg-surface border border-border border-l-[3px] border-l-teal-500 rounded-[10px] p-4">
                <div className="mb-1">{actionIcons[i] || actionIcons[0]}</div>
                <div className="text-sm text-text-secondary leading-relaxed mt-2">
                  {step}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── STRUCTURAL RISK BANNER ── */}
      {structuralRisk && (
        <div className="border-l-4 border-teal-500 bg-teal-500/[0.05] rounded-r-[10px] px-5 py-[18px] mb-5">
          <div className="flex items-center gap-2 mb-2.5">
            <Shield className="w-[18px] h-[18px] text-teal-500" />
            <span className="text-[11px] uppercase text-teal-500 tracking-[0.15em] font-semibold">
              Structural Risk Identified
            </span>
          </div>
          <div className="text-[15px] text-text-secondary leading-relaxed">
            {structuralRisk}
          </div>
        </div>
      )}

      {/* ── TAB BAR ── */}
      <div className="flex border-b border-border mt-6">
        {tabs.filter(t => t.show).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-5 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer',
              activeTab === tab.id
                ? 'text-text-primary border-b-teal-500'
                : 'text-text-muted border-b-transparent hover:text-text-secondary',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB PANELS ── */}
      <div className="mt-5">
        {activeTab === 'summary' && (
          <SummaryTab
            killSheet={killSheet}
            stressTestResults={stressTestResults}
            confidencePercent={confidencePercent}
            mediatorConsensus={mediatorConsensus}
          />
        )}
        {activeTab === 'ghost-deck' && (
          <GhostDeckTab slides={ghostDeck} />
        )}
        {activeTab === 'debate' && (
          <DebateTab
            debateArguments={debateArguments}
            debateRounds={debateRounds}
            mediatorConsensus={mediatorConsensus}
          />
        )}
        {activeTab === 'workstreams' && (
          <WorkstreamsTab workstreams={workstreams} />
        )}
      </div>

      {/* ── FOOTER ── */}
      {onNewResearch && (
        <div className="mt-8 flex items-center gap-3">
          <button
            onClick={onNewResearch}
            className="flex items-center gap-2 px-4 py-2 bg-surface hover:bg-surface-light border border-border rounded-lg text-text-secondary text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            New Analysis
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUMMARY TAB
// ============================================================================

function SummaryTab({
  killSheet,
  stressTestResults,
  confidencePercent,
  mediatorConsensus,
}: {
  killSheet?: StrategicDeliverable['killSheet'];
  stressTestResults: StressTestResult[];
  confidencePercent?: number;
  mediatorConsensus?: MediatorConsensus;
}) {
  const risks = killSheet?.risks || [];
  const opportunities = killSheet?.opportunities || [];

  return (
    <div>
      {/* Confidence Gauge */}
      {confidencePercent !== undefined && (
        <div className="mb-5">
          <div className="w-full h-1.5 bg-surface-raised rounded-full relative overflow-visible">
            <div
              className="h-full rounded-full"
              style={{
                width: `${confidencePercent}%`,
                background: 'linear-gradient(to right, #ef4444 0%, #ef4444 20%, #f59e0b 45%, #22c55e 80%)',
              }}
            />
            <div
              className="absolute -top-1 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-white"
              style={{ left: `${confidencePercent}%`, transform: 'translateX(-50%)' }}
            />
          </div>
          <div className="mt-2.5 text-sm text-text-secondary leading-relaxed">
            {confidencePercent}% &mdash; {confidencePercent >= 75 ? 'High' : confidencePercent >= 50 ? 'Moderate' : 'Low'} confidence.
            {mediatorConsensus?.rationale && ` ${mediatorConsensus.rationale.slice(0, 120)}...`}
          </div>
        </div>
      )}

      {/* Headwinds & Tailwinds */}
      {(risks.length > 0 || opportunities.length > 0) && (
        <div className="flex gap-4 mb-5">
          {/* Headwinds */}
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-500 mb-3">
              <TrendingDown className="w-4 h-4" />
              Headwinds
            </div>
            {risks.map((risk, i) => (
              <div key={i} className="rounded-lg p-3.5 mb-2.5 bg-red-500/[0.04] border border-red-500/[0.12]">
                <div className="text-sm text-text-primary font-medium">{risk}</div>
              </div>
            ))}
          </div>

          {/* Tailwinds */}
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-green-500 mb-3">
              <TrendingUp className="w-4 h-4" />
              Tailwinds
            </div>
            {opportunities.map((opp, i) => (
              <div key={i} className="rounded-lg p-3.5 mb-2.5 bg-green-500/[0.04] border border-green-500/[0.12]">
                <div className="text-sm text-text-primary font-medium">{opp}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stress-Tested Assumptions */}
      {stressTestResults.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-text-secondary mb-3.5">
            Stress-Tested Assumptions
          </div>
          {stressTestResults.map((test, i) => {
            const pct = Math.round(test.confidence * 100);
            const colorClass = pct >= 60 ? 'text-green-500' : 'text-amber-500';
            const barClass = pct >= 60 ? 'bg-green-500' : 'bg-amber-500';
            return (
              <div key={i} className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-text-secondary">{test.assumptionStatement}</span>
                  <span className={cn('text-[13px] font-semibold', colorClass)}>{pct}% holds</span>
                </div>
                <div className="w-full h-1 bg-surface-raised rounded-full">
                  <div className={cn('h-full rounded-full transition-all duration-500', barClass)} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// GHOST DECK TAB
// ============================================================================

function GhostDeckTab({ slides }: { slides: GhostDeckSlide[] }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const current = slides[activeSlide];

  if (!current) return <div className="text-text-muted text-sm">No ghost deck slides available.</div>;

  const slideColorMap: Record<string, string> = {
    executive_summary: 'bg-teal-500',
    problem: 'bg-blue-500',
    market: 'bg-blue-500',
    strategy: 'bg-green-500',
    risk: 'bg-amber-500',
    roadmap: 'bg-teal-500',
  };

  const barColor = slideColorMap[current.slideType] || 'bg-verdict-primary';

  return (
    <div className="flex gap-5">
      {/* Sidebar */}
      <div className="w-[200px] flex-shrink-0 flex flex-col gap-2">
        {slides.map((slide, i) => (
          <button
            key={i}
            onClick={() => setActiveSlide(i)}
            className={cn(
              'text-left bg-surface-light border border-border rounded-lg px-3 py-2.5 transition-colors cursor-pointer',
              activeSlide === i ? 'border-verdict-primary bg-verdict-primary/[0.06]' : 'hover:bg-surface-raised',
            )}
          >
            <div className="text-[10px] text-text-muted font-semibold uppercase tracking-wider">
              Slide {slide.slideNumber}
            </div>
            <div className={cn('text-xs mt-1 font-medium', activeSlide === i ? 'text-text-primary' : 'text-text-secondary')}>
              {slide.title}
            </div>
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className={cn('h-1 w-full', barColor)} />
          <div className="p-6 pb-7">
            <div className="text-xl font-bold text-text-primary mb-4">{current.title}</div>
            {current.headlineInsight && (
              <div className="text-sm text-verdict-primary font-medium mb-4 italic">
                {current.headlineInsight}
              </div>
            )}
            <ul className="space-y-2.5">
              {current.keyPoints.map((point, i) => (
                <li key={i} className="relative pl-[18px] text-sm text-text-secondary leading-relaxed">
                  <span className="absolute left-0 top-[8px] w-[5px] h-[5px] rounded-full bg-text-muted" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-4">
          <span className="text-[13px] text-text-muted">
            Slide {activeSlide + 1} of {slides.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveSlide(Math.max(0, activeSlide - 1))}
              disabled={activeSlide === 0}
              className="w-[34px] h-[34px] flex items-center justify-center bg-surface-raised border border-border rounded-lg text-text-secondary hover:bg-surface-raised hover:text-text-primary disabled:opacity-30 transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setActiveSlide(Math.min(slides.length - 1, activeSlide + 1))}
              disabled={activeSlide === slides.length - 1}
              className="w-[34px] h-[34px] flex items-center justify-center bg-surface-raised border border-border rounded-lg text-text-secondary hover:bg-surface-raised hover:text-text-primary disabled:opacity-30 transition-colors cursor-pointer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DEBATE TAB
// ============================================================================

function DebateTab({
  debateArguments,
  debateRounds: _debateRounds,
  mediatorConsensus,
}: {
  debateArguments: DebateArgument[];
  debateRounds: number;
  mediatorConsensus?: MediatorConsensus;
}) {
  // Group arguments by round
  const rounds = useMemo(() => {
    const grouped: Record<number, DebateArgument[]> = {};
    for (const arg of debateArguments) {
      if (!grouped[arg.roundNumber]) grouped[arg.roundNumber] = [];
      grouped[arg.roundNumber].push(arg);
    }
    return Object.entries(grouped)
      .map(([round, args]) => ({ round: parseInt(round, 10), args }))
      .sort((a, b) => a.round - b.round);
  }, [debateArguments]);

  // Unique debaters
  const debaters = useMemo(() => {
    const seen = new Map<string, DebateArgument>();
    for (const arg of debateArguments) {
      if (!seen.has(arg.debaterName)) seen.set(arg.debaterName, arg);
    }
    return Array.from(seen.values());
  }, [debateArguments]);

  const debaterColors: Record<number, string> = { 0: 'border-l-blue-500', 1: 'border-l-verdict-primary', 2: 'border-l-red-500' };
  const debaterTextColors: Record<number, string> = { 0: 'text-blue-500', 1: 'text-verdict-primary', 2: 'text-red-500' };

  return (
    <div>
      {/* Debater cards */}
      {debaters.length > 0 && (
        <div className="flex gap-3 mb-5">
          {debaters.map((d, i) => (
            <div key={i} className={cn('flex-1 bg-surface border border-border rounded-[10px] p-3 pl-4 border-l-[3px]', debaterColors[i % 3] || 'border-l-text-muted')}>
              <div className={cn('text-sm font-semibold flex items-center gap-2', debaterTextColors[i % 3] || 'text-text-primary')}>
                {d.debaterName}
              </div>
              <div className="text-xs text-text-muted mt-1">{d.debaterRole}</div>
            </div>
          ))}
        </div>
      )}

      {/* Rounds */}
      <div className="flex flex-col gap-2">
        {rounds.map(({ round, args }) => (
          <DebateRound key={round} round={round} args={args} debaters={debaters} />
        ))}
      </div>

      {/* Mediator consensus */}
      {mediatorConsensus && (
        <div className="mt-6 bg-surface border border-border rounded-[10px] p-5">
          <div className="text-[11px] uppercase text-text-muted tracking-[0.15em] font-semibold mb-3">
            Mediator Consensus
          </div>
          {mediatorConsensus.confidence > 0 && (
            <div className="mb-3">
              <div className="w-full h-1.5 rounded-full" style={{ background: 'linear-gradient(to right, #ef4444, #f59e0b, #22c55e)' }}>
                <div
                  className="relative h-full"
                  style={{ width: `${Math.round(mediatorConsensus.confidence * 100)}%` }}
                >
                  <div className="absolute -right-1.5 -top-1 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-white" />
                </div>
              </div>
            </div>
          )}
          {mediatorConsensus.finalRecommendation && (
            <div className="text-sm text-text-secondary leading-relaxed">
              {mediatorConsensus.finalRecommendation}
            </div>
          )}
          {mediatorConsensus.keyAgreements.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] text-text-muted font-medium uppercase mb-1.5">Key Agreements</div>
              <ul className="space-y-1">
                {mediatorConsensus.keyAgreements.map((agreement, i) => (
                  <li key={i} className="text-sm text-text-secondary pl-3 relative">
                    <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-green-500" />
                    {agreement}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {mediatorConsensus.unresolvedTensions.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] text-text-muted font-medium uppercase mb-1.5">Unresolved Tensions</div>
              <ul className="space-y-1">
                {mediatorConsensus.unresolvedTensions.map((tension, i) => (
                  <li key={i} className="text-sm text-amber-500/80 pl-3 relative">
                    <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-amber-500" />
                    {tension}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DebateRound({
  round,
  args,
  debaters,
}: {
  round: number;
  args: DebateArgument[];
  debaters: DebateArgument[];
}) {
  const [isOpen, setIsOpen] = useState(round === 1);
  const debaterColorIndex = (name: string) => debaters.findIndex(d => d.debaterName === name);
  const argBgColors: Record<number, string> = { 0: 'border-l-blue-500', 1: 'border-l-verdict-primary', 2: 'border-l-red-500' };
  const argTextColors: Record<number, string> = { 0: 'text-blue-500', 1: 'text-verdict-primary', 2: 'text-red-500' };

  return (
    <div className="border border-border rounded-[10px] overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-surface-light transition-colors cursor-pointer select-none"
      >
        <span className="text-sm font-semibold text-text-primary">Round {round}</span>
        <ChevronDown className={cn(
          'w-[18px] h-[18px] text-text-muted transition-transform duration-250',
          isOpen && 'rotate-180',
        )} />
      </button>
      <div className={cn(
        'overflow-hidden transition-all duration-300',
        isOpen ? 'max-h-[2000px]' : 'max-h-0',
      )}>
        <div className="px-4 pb-4 pt-1 flex flex-col gap-2.5">
          {args.map((arg, i) => {
            const idx = debaterColorIndex(arg.debaterName);
            return (
              <div key={i} className={cn('rounded-lg p-3 pl-4 bg-surface-light border-l-[3px]', argBgColors[idx % 3] || 'border-l-text-muted')}>
                <div className={cn('text-[11px] font-semibold uppercase tracking-wider mb-1.5', argTextColors[idx % 3] || 'text-text-secondary')}>
                  {arg.debaterName}
                </div>
                <div className="text-sm text-text-secondary leading-relaxed italic">
                  &ldquo;{arg.argument}&rdquo;
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// WORKSTREAMS TAB
// ============================================================================

function WorkstreamsTab({ workstreams }: { workstreams: StrategicWorkstream[] }) {
  return (
    <div className="flex flex-col gap-3">
      {workstreams.map((ws) => (
        <WorkstreamCard key={ws.id} workstream={ws} />
      ))}
    </div>
  );
}

function WorkstreamCard({ workstream }: { workstream: StrategicWorkstream }) {
  const [isOpen, setIsOpen] = useState(false);
  const agentCount = workstream.agents.length;
  const isComplete = workstream.status === 'complete';

  return (
    <div className="bg-surface border border-border rounded-[10px] overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-surface-light transition-colors cursor-pointer select-none"
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <Layers className="w-4 h-4 text-text-muted flex-shrink-0" />
          <span className="text-[15px] font-semibold text-text-primary truncate">{workstream.name}</span>
          <span className="text-[11px] text-text-muted bg-surface-raised border border-border rounded-md px-2 py-0.5 font-medium flex-shrink-0">
            {agentCount} agent{agentCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {isComplete && <ShieldCheck className="w-4 h-4 text-teal-500" />}
          <ChevronDown className={cn(
            'w-[18px] h-[18px] text-text-muted transition-transform duration-300',
            isOpen && 'rotate-180',
          )} />
        </div>
      </button>

      <div className={cn(
        'overflow-hidden transition-all duration-300',
        isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0',
      )}>
        <div className="px-4 pb-4">
          <div className="border-t border-border pt-3">
            {/* Key questions */}
            {workstream.keyQuestions.length > 0 && (
              <div className="mb-3">
                <div className="text-[11px] text-text-muted font-medium uppercase tracking-wider mb-1.5">Key Questions</div>
                <ul className="space-y-1">
                  {workstream.keyQuestions.map((q, i) => (
                    <li key={i} className="text-sm text-text-secondary pl-3 relative">
                      <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-text-muted" />
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Key findings */}
            {workstream.keyFindings.length > 0 && (
              <div className="mb-3">
                <div className="text-[11px] text-text-muted font-medium uppercase tracking-wider mb-1.5">Key Findings</div>
                <ul className="space-y-1">
                  {workstream.keyFindings.map((f, i) => (
                    <li key={i} className="text-sm text-text-secondary pl-3 relative">
                      <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-teal-500" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Synthesis preview */}
            {workstream.synthesisPreview && (
              <div className="text-sm text-text-secondary leading-relaxed border-l-2 border-border-light pl-3 italic">
                {workstream.synthesisPreview}
              </div>
            )}

            {/* Meta */}
            <div className="mt-3 text-xs text-text-muted">
              {workstream.totalCitations} citation{workstream.totalCitations !== 1 ? 's' : ''}
              {workstream.latencySeconds && <> &bull; {Math.round(workstream.latencySeconds)}s</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
